import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Download, LogOut, RefreshCw, Lock } from "lucide-react";
import FlowerButton from "@/components/FlowerButton";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "mm_admin_secret";

export default function AdminNewsletter() {
  const [secret, setSecret] = useState(() => sessionStorage.getItem(KEY) || "");
  const [input, setInput] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (s) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/newsletter/subscribers`, { headers: { "X-Admin-Secret": s } });
      if (res.status === 401) throw new Error("unauthorized");
      if (!res.ok) throw new Error("failed");
      setRows((await res.json()).subscribers);
      sessionStorage.setItem(KEY, s);
      setSecret(s);
    } catch (e) {
      sessionStorage.removeItem(KEY);
      setSecret("");
      setRows(null);
      toast.error(e.message === "unauthorized" ? "Mot de passe incorrect ✿" : "Impossible de charger la liste.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (secret) load(secret); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = async () => {
    const res = await fetch(`${API}/newsletter/subscribers.csv`, { headers: { "X-Admin-Secret": secret } });
    if (!res.ok) { toast.error("Export impossible."); return; }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url; a.download = "potes-qui-papotent.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const logout = () => { sessionStorage.removeItem(KEY); setSecret(""); setRows(null); setInput(""); };

  if (!secret) {
    return (
      <div className="max-w-md mx-auto px-6 py-24" data-testid="admin-login">
        <div className="rounded-[2rem] border-2 border-dashed border-[#D1D5CB] bg-[#FAF9F6] p-8 text-center">
          <Lock className="w-8 h-8 mx-auto text-[#78353A] mb-3" />
          <p className="font-cursive text-2xl text-[#78353A]/70">Espace privé</p>
          <h1 className="font-cursive text-5xl text-[#78353A] leading-none mb-6">Les potes qui papotent</h1>
          <p className="mt-2 text-sm"><Link to="/admin/commandes" className="underline decoration-dotted text-[#78353A]">Voir les commandes →</Link></p>
          <form onSubmit={(e) => { e.preventDefault(); if (input) load(input); }} className="flex flex-col gap-3">
            <input type="password" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Mot de passe" autoComplete="current-password"
              data-testid="admin-secret-input"
              className="px-5 py-3 rounded-full bg-white border-2 border-dashed border-[#D1D5CB] text-[#4A3B32] focus:outline-none focus:border-[#78353A]" />
            <FlowerButton type="submit" disabled={loading || !input} data-testid="admin-login-btn" className="!py-3 !px-6 !text-lg mx-auto">
              {loading ? "…" : "Entrer"}
            </FlowerButton>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12" data-testid="admin-newsletter">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-cursive text-2xl text-[#78353A]/70">Espace privé</p>
          <h1 className="font-cursive text-5xl md:text-6xl text-[#78353A] leading-none">Les potes qui papotent</h1>
          <p className="mt-2 text-[#4A3B32]/80" data-testid="admin-count">{rows ? `${rows.length} inscrit${rows.length > 1 ? "s" : ""}` : "Chargement…"}</p>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => load(secret)} data-testid="admin-refresh" className="w-11 h-11 rounded-full bg-[#FAF9F6] border border-[#D1D5CB] flex items-center justify-center text-[#78353A] hover:bg-[#EAECE1]" title="Actualiser">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <FlowerButton variant="sage" onClick={exportCsv} data-testid="admin-export-csv" className="!py-3 !px-6 !text-lg">
            <span className="inline-flex items-center gap-2"><Download className="w-4 h-4" /> Exporter en CSV</span>
          </FlowerButton>
          <button type="button" onClick={logout} data-testid="admin-logout" className="w-11 h-11 rounded-full bg-[#FAF9F6] border border-[#D1D5CB] flex items-center justify-center text-[#78353A] hover:bg-[#FADADD]" title="Se déconnecter">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="mt-8 rounded-[2rem] border-2 border-dashed border-[#D1D5CB] bg-[#FAF9F6] overflow-hidden">
        <table className="w-full text-left" data-testid="admin-table">
          <thead className="bg-[#EAECE1] font-cursive text-xl text-[#78353A]">
            <tr><th className="px-6 py-3">Email</th><th className="px-6 py-3 hidden sm:table-cell">Source</th><th className="px-6 py-3">Inscrit le</th></tr>
          </thead>
          <tbody className="text-[#4A3B32]">
            {rows?.map((r) => (
              <tr key={r.email} className="border-t border-dashed border-[#D1D5CB]">
                <td className="px-6 py-3 break-all">{r.email}</td>
                <td className="px-6 py-3 hidden sm:table-cell text-[#4A3B32]/70">{r.source}</td>
                <td className="px-6 py-3 text-sm text-[#4A3B32]/80">{r.subscribed_at ? new Date(r.subscribed_at + (r.subscribed_at.endsWith("Z") ? "" : "Z")).toLocaleString("fr-FR") : ""}</td>
              </tr>
            ))}
            {rows && rows.length === 0 && (
              <tr><td colSpan={3} className="px-6 py-10 text-center font-cursive text-2xl text-[#78353A]/60">Personne ne papote encore ✿</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
